/**
 * Phase1PreCommitSafetyBody (extracted from ReleaseOpsTab.jsx in iter84.59.21)
 *
 * Body of the Phase 1 ("Preview · pre-commit safety") collapsible
 * section. The `<CollapsiblePhaseSection number="1" ...>` wrapper stays
 * in the orchestrator (per the iter84.59.18/19 pre-extraction checklist:
 * call-site tests pin `number="1"` against the orchestrator); only the
 * children move here.
 *
 * iter84.71 — Two operator-UX changes:
 *   1. Each check+gate pair is wrapped in a numbered `SubStep` (1.1–1.5)
 *      with a left rail + extra spacing, so it's obvious which gate row
 *      belongs to which card.
 *   2. The Post-Deploy Auto-Verify RUNNER card moved here from Phase 4
 *      (Audit). Its gate row (`pre_deploy:auto_verify_preview`) already
 *      lived in Phase 1 — having the runner three phases away was the
 *      confusion the operator reported. Runner + gate now sit together
 *      as sub-step 1.5. (On production the runner renders in Phase 5 —
 *      see Phase5PostDeploymentBody.jsx.)
 *
 * Renders, in order:
 *   1.1 Parity Sweep card + gate (`pre_deploy:parity_sweep_clear`).
 *   1.2 Deployment Readiness banner + gate (`pre_deploy:readiness_green`).
 *   1.3 Values Engine card + gate (`pre_deploy:values_check_preview`).
 *   1.4 Obligations Engine card (informational — no gate).
 *   1.5 Vendor Health card + gate (`pre_deploy:vendor_health_green`).
 *   1.6 Auto-Verify runner card + InlineGateCard (`pre_deploy:auto_verify_preview`).
 *   1.7 Acceptance Checklist card + gate (`pre_deploy:preview_acceptance`) —
 *       always manual disposition, never auto-verified.
 *
 * Each card emits a verdict via `onVerdictResolved` that the orchestrator
 * maps through `autoFlipGate` so the corresponding gate auto-flips.
 *
 * iter87.209 — Split the combined 1.4 sub-step (Obligations + Vendor
 * Health sharing one number) into its own 1.4/1.5 pair so each card
 * reads as one visual unit with its own gate row, matching every other
 * sub-step. Auto-Verify and Acceptance Checklist shift to 1.6/1.7.
 */
import { ParitySweepCard } from "@/components/site-admin/ReleaseOpsAuditCards";
import DeploymentReadinessBanner from "@/components/site-admin/DeploymentReadinessBanner";
import { ValuesEngineCard, UATRunbookCard } from "@/components/site-admin/ValuesAndUATCards";
import { ObligationsEngineCard } from "@/components/site-admin/ObligationsEngineCard";
import PostDeployAutoVerifyCard from "@/components/site-admin/PostDeployAutoVerifyCard";
import { GateButtons } from "@/components/site-admin/GateButtons";
import { InlineGateCard } from "@/components/site-admin/RunbookPhaseCards";
import VendorHealthCard from "@/components/site-admin/release-ops/VendorHealthCard";
import { checkOf } from "@/components/site-admin/release-ops/CollapsiblePhaseSection";

// iter84.71 — Numbered sub-step wrapper: left rail + step label so each
// check card and its gate row read as one visual unit. iter86.6 —
// generalized (phase + accent props) and exported for Phase 2/3 bodies.
export function SubStep({ n, title, children, phase = 1, accentClass = "text-cyber" }) {
    return (
        <div
            data-testid={`phase${phase}-substep-${n}`}
            className="border-l-2 border-[#2a2a2a] pl-3 sm:pl-4 py-1 space-y-3"
        >
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
                <span className={accentClass}>{phase}.{n}</span> · {title}
            </p>
            {children}
        </div>
    );
}

export function Phase1PreCommitSafetyBody({
    gateCtx,
    activePromotion,
    environment,
    autoFlipGate,
    isArmed,
    onOpenGameOps,
    currentIter,
}) {
    return (
        <div className="space-y-6">
            <SubStep n={1} title="Pre-deploy parity sweep">
                {/* Parity Sweep + gate — auto-flips parity_sweep_clear
                    gate on verdict (clean → Approved, warn → Waived,
                    fail → leaves Pending for human review). */}
                <ParitySweepCard
                    environment={environment}
                    onOpenGameOps={onOpenGameOps}
                    onVerdictResolved={({ overall, summary }) =>
                        autoFlipGate({
                            section: "pre_deploy",
                            itemId: "parity_sweep_clear",
                            verdict: overall,
                            summary,
                        })
                    }
                />
                <GateButtons
                    {...gateCtx}
                    section="pre_deploy"
                    itemId="parity_sweep_clear"
                    label="Pre-deploy sweep clean (or issues waived)"
                    required
                    state={checkOf(activePromotion, "pre_deploy", "parity_sweep_clear")}
                />
            </SubStep>

            <SubStep n={2} title="Deployment readiness">
                {/* Deployment Readiness banner + gate. iter84.56.7-f —
                    Auto-flips readiness_green gate on verdict
                    (ok → Approved, warn → Waived with reason,
                    fail → stays Pending). */}
                <DeploymentReadinessBanner
                    onVerdictResolved={({ overall, warn_count, fail_count }) =>
                        autoFlipGate({
                            section: "pre_deploy",
                            itemId: "readiness_green",
                            verdict: overall,
                            summary: { warn_count, fail_count },
                        })
                    }
                />
                <GateButtons
                    {...gateCtx}
                    section="pre_deploy"
                    itemId="readiness_green"
                    label="Deployment Readiness banner = GREEN"
                    required
                    state={checkOf(activePromotion, "pre_deploy", "readiness_green")}
                />
            </SubStep>

            <SubStep n={3} title="Values Engine">
                {/* iter84.56.7-g — Values Engine restored as a gated
                    card. The card emits its verdict and the parent
                    auto-flips the values_check_preview gate (clear →
                    Approved, ack_required → Waived with warn count,
                    block_arm → stays Pending). Hidden on production
                    because Values Engine is a preview-side validator. */}
                <ValuesEngineCard
                    environment={environment}
                    autoPollMs={isArmed ? 60000 : 0}
                    onVerdictResolved={({ overall, summary }) => {
                        // Map Values Engine's vocabulary to the shared helper:
                        // clear → "clear", ack_required → "warn", block_arm → "fail".
                        const verdict =
                            overall === "clear" ? "clear" :
                            overall === "ack_required" ? "warn" :
                            "fail";
                        autoFlipGate({
                            section: "pre_deploy",
                            itemId: "values_check_preview",
                            verdict,
                            summary: { warn_count: summary?.warn || 0 },
                        });
                    }}
                />
                <GateButtons
                    {...gateCtx}
                    section="pre_deploy"
                    itemId="values_check_preview"
                    label="Values Engine = clear (or warnings waived)"
                    required
                    state={checkOf(activePromotion, "pre_deploy", "values_check_preview")}
                />
            </SubStep>

            <SubStep n={4} title="Obligations Engine">
                {/* iter84.61 — Obligations Engine: risk-if-deployed posture of
                    every externally-imposed requirement (compliance_state ×
                    severity), grouped by obligation_group. Informational
                    sibling of the Values Engine card — no gate. */}
                <ObligationsEngineCard />
            </SubStep>

            <SubStep n={5} title="Vendor Health">
                {/* iter85.5 — Vendor Health: renewal radar + Resend domain
                    verification + API-Sports quota. Auto-runs on load and
                    auto-flips `pre_deploy:vendor_health_green`:
                      * pass → Approved · warn → Waived · fail → stays Pending
                        (locks the Commit gate). */}
                <VendorHealthCard
                    onVerdictResolved={({ overall, summary }) =>
                        autoFlipGate({
                            section: "pre_deploy",
                            itemId: "vendor_health_green",
                            verdict: overall,
                            summary,
                        })
                    }
                />
                <GateButtons
                    {...gateCtx}
                    section="pre_deploy"
                    itemId="vendor_health_green"
                    label="Vendor Health = green (or warnings waived)"
                    required
                    state={checkOf(activePromotion, "pre_deploy", "vendor_health_green")}
                />
            </SubStep>

            <SubStep n={6} title="Auto-Verify on preview">
                {/* iter84.71 — The Auto-Verify RUNNER moved here from Phase 4
                    so it sits next to the gate it feeds. Verdict auto-flips
                    `pre_deploy:auto_verify_preview`:
                      * pass → Approved
                      * warn → Waived (with warn-count summary)
                      * fail → stays Pending (locks the Commit gate) */}
                <PostDeployAutoVerifyCard
                    onVerdictResolved={({ overall, summary }) =>
                        autoFlipGate({
                            section: "pre_deploy",
                            itemId: "auto_verify_preview",
                            verdict: overall,
                            summary,
                        })
                    }
                />
                <InlineGateCard
                    title="Auto-Verify on preview = pass (or warnings waived)"
                    description="auto-flips on the runner card's verdict above — RED keeps this Pending and locks the Commit gate"
                    {...gateCtx}
                    section="pre_deploy"
                    itemId="auto_verify_preview"
                    label="Auto-Verify on preview"
                    required
                    state={checkOf(activePromotion, "pre_deploy", "auto_verify_preview")}
                />
            </SubStep>

            {/* iter86.8 — Preview acceptance (replaces the retired
                post-deploy UAT walk). The acceptance checklist is
                generated from the Feature Registry by the agent when the
                iter closes (Product Ops → Catalog → UAT generator is the
                manual fallback). Operator verifies each shipped feature
                meets its criteria HERE, in preview, before ARM. Waive
                with a reason for fix-only / infra iters. Never auto-
                verified — this sub-step is always a manual disposition. */}
            <SubStep n={7} title="Acceptance Checklist — shipped features meet their criteria">
                <UATRunbookCard environment={environment} currentIter={currentIter} />
                <GateButtons
                    {...gateCtx}
                    section="pre_deploy"
                    itemId="preview_acceptance"
                    label="Preview acceptance — all checklist items verified (or waived for fix-only iters)"
                    required
                    state={checkOf(activePromotion, "pre_deploy", "preview_acceptance")}
                />
            </SubStep>
        </div>
    );
}
